<div class="container">

  <header class="page-heading">
    <h1>Income Tax Calculator (Old vs New Regime)</h1>
    <p>Compare your income tax liability under the Old and New tax regimes for FY 2025-26 and find out which regime saves you more.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">Income Tax Details (FY 2025-26)</div>

    <!-- Age Category -->
    <div class="field-group">
      <!-- Group heading for the age-category toggle buttons below, wired via aria-labelledby (not a single-control label) -->
      <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
      <label class="field-label" id="age-category-label">Age Category</label>
      <div class="mode-toggle" role="group" aria-labelledby="age-category-label">
        <button type="button" class="mode-btn" [class.active]="ageCategory==='below60'" (click)="setAgeCategory('below60')">
          Below 60
        </button>
        <button type="button" class="mode-btn" [class.active]="ageCategory==='senior'" (click)="setAgeCategory('senior')">
          Senior (60–80)
        </button>
        <button type="button" class="mode-btn" [class.active]="ageCategory==='superSenior'" (click)="setAgeCategory('superSenior')">
          Super Senior (80+)
        </button>
      </div>
    </div>

    <!-- ── INCOME ─────────────────────────────────────────────────── -->
    <div class="subsection-title">Income Details</div>

    <div class="field-group" [class.has-error]="hasError('annualSalary')">
      <label class="field-label" for="annualSalary">Annual Salary Income</label>
      <div class="input-shell prefix">
        <span class="affix">₹</span>
        <input id="annualSalary" type="number"
          [(ngModel)]="annualSalary"
          [min]="C.annualSalary.min" [max]="C.annualSalary.max" [step]="C.annualSalary.step"
          (ngModelChange)="onFieldChange('annualSalary')"
          (blur)="onFieldBlur('annualSalary')"
          placeholder="e.g. 12,00,000"
          autocomplete="off"/>
      </div>
      <div class="field-hint">Gross salary before any deductions, per Form 16.</div>
      @if (hasError('annualSalary')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('annualSalary') }}
        </div>
      }
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('interestIncome')">
        <label class="field-label" for="interestIncome">
          Interest Income
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="interestIncome" type="number"
            [(ngModel)]="interestIncome"
            [min]="C.interestIncome.min" [max]="C.interestIncome.max" [step]="C.interestIncome.step"
            (ngModelChange)="onFieldChange('interestIncome')"
            (blur)="onFieldBlur('interestIncome')"
            placeholder="e.g. 20,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Savings account & FD interest.</div>
        @if (hasError('interestIncome')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('interestIncome') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('otherIncome')">
        <label class="field-label" for="otherIncome">
          Other Income
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="otherIncome" type="number"
            [(ngModel)]="otherIncome"
            [min]="C.otherIncome.min" [max]="C.otherIncome.max" [step]="C.otherIncome.step"
            (ngModelChange)="onFieldChange('otherIncome')"
            (blur)="onFieldBlur('otherIncome')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Freelance, dividends, etc.</div>
        @if (hasError('otherIncome')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('otherIncome') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('rentalIncome')">
        <label class="field-label" for="rentalIncome">
          Rental Income
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="rentalIncome" type="number"
            [(ngModel)]="rentalIncome"
            [min]="C.rentalIncome.min" [max]="C.rentalIncome.max" [step]="C.rentalIncome.step"
            (ngModelChange)="onFieldChange('rentalIncome')"
            (blur)="onFieldBlur('rentalIncome')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Annual rent from let-out property.</div>
        @if (hasError('rentalIncome')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('rentalIncome') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('homeLoanInterestLetOut')">
        <label class="field-label" for="homeLoanInterestLetOut">
          Home Loan Interest (Let-out)
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="homeLoanInterestLetOut" type="number"
            [(ngModel)]="homeLoanInterestLetOut"
            [min]="C.homeLoanInterestLetOut.min" [max]="C.homeLoanInterestLetOut.max" [step]="C.homeLoanInterestLetOut.step"
            (ngModelChange)="onFieldChange('homeLoanInterestLetOut')"
            (blur)="onFieldBlur('homeLoanInterestLetOut')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Interest paid on loan for the rented-out property.</div>
        @if (hasError('homeLoanInterestLetOut')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('homeLoanInterestLetOut') }}
          </div>
        }
      </div>
    </div>

    <!-- ── DEDUCTIONS (OLD REGIME) ────────────────────────────────── -->
    <div class="subsection-title">Deductions <span class="optional-tag">old regime only</span></div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('hraExemption')">
        <label class="field-label" for="hraExemption">
          HRA Exemption
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="hraExemption" type="number"
            [(ngModel)]="hraExemption"
            [min]="C.hraExemption.min" [max]="C.hraExemption.max" [step]="C.hraExemption.step"
            (ngModelChange)="onFieldChange('hraExemption')"
            (blur)="onFieldBlur('hraExemption')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Exempt portion of House Rent Allowance.</div>
        @if (hasError('hraExemption')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('hraExemption') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('section80C')">
        <label class="field-label" for="section80C">Section 80C</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="section80C" type="number"
            [(ngModel)]="section80C"
            [min]="C.section80C.min" [max]="C.section80C.max" [step]="C.section80C.step"
            (ngModelChange)="onFieldChange('section80C')"
            (blur)="onFieldBlur('section80C')"
            placeholder="e.g. 1,50,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">EPF, PPF, ELSS, LIC, tuition fees etc. Capped at {{ fmt.currency(C.section80C.max) }}.</div>
        @if (hasError('section80C')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('section80C') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('section80CCD1B')">
        <label class="field-label" for="section80CCD1B">
          Section 80CCD(1B) — NPS
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="section80CCD1B" type="number"
            [(ngModel)]="section80CCD1B"
            [min]="C.section80CCD1B.min" [max]="C.section80CCD1B.max" [step]="C.section80CCD1B.step"
            (ngModelChange)="onFieldChange('section80CCD1B')"
            (blur)="onFieldBlur('section80CCD1B')"
            placeholder="e.g. 50,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Additional NPS contribution, over and above 80C. Capped at {{ fmt.currency(C.section80CCD1B.max) }}.</div>
        @if (hasError('section80CCD1B')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('section80CCD1B') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('section80D')">
        <label class="field-label" for="section80D">
          Section 80D — Health Insurance
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="section80D" type="number"
            [(ngModel)]="section80D"
            [min]="C.section80D.min" [max]="C.section80D.max" [step]="C.section80D.step"
            (ngModelChange)="onFieldChange('section80D')"
            (blur)="onFieldBlur('section80D')"
            placeholder="e.g. 25,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Health insurance premium. Capped at {{ fmt.currency(ageCategory === 'below60' ? 25000 : 50000) }} for your age category.</div>
        @if (hasError('section80D')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('section80D') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('homeLoanInterestSelfOccupied')">
        <label class="field-label" for="homeLoanInterestSelfOccupied">
          Home Loan Interest (Self-occupied)
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="homeLoanInterestSelfOccupied" type="number"
            [(ngModel)]="homeLoanInterestSelfOccupied"
            [min]="C.homeLoanInterestSelfOccupied.min" [max]="C.homeLoanInterestSelfOccupied.max" [step]="C.homeLoanInterestSelfOccupied.step"
            (ngModelChange)="onFieldChange('homeLoanInterestSelfOccupied')"
            (blur)="onFieldBlur('homeLoanInterestSelfOccupied')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Section 24(b). Capped at {{ fmt.currency(C.homeLoanInterestSelfOccupied.max) }}.</div>
        @if (hasError('homeLoanInterestSelfOccupied')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('homeLoanInterestSelfOccupied') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('section80E')">
        <label class="field-label" for="section80E">
          Section 80E — Education Loan Interest
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="section80E" type="number"
            [(ngModel)]="section80E"
            [min]="C.section80E.min" [max]="C.section80E.max" [step]="C.section80E.step"
            (ngModelChange)="onFieldChange('section80E')"
            (blur)="onFieldBlur('section80E')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">No upper limit under Section 80E.</div>
        @if (hasError('section80E')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('section80E') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group" [class.has-error]="hasError('section80G')">
      <label class="field-label" for="section80G">
        Section 80G — Donations
        <span class="optional-tag">optional</span>
      </label>
      <div class="input-shell prefix">
        <span class="affix">₹</span>
        <input id="section80G" type="number"
          [(ngModel)]="section80G"
          [min]="C.section80G.min" [max]="C.section80G.max" [step]="C.section80G.step"
          (ngModelChange)="onFieldChange('section80G')"
          (blur)="onFieldBlur('section80G')"
          placeholder="e.g. 0"
          autocomplete="off"/>
      </div>
      <div class="field-hint">Eligible donations to approved funds/charities.</div>
      @if (hasError('section80G')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('section80G') }}
        </div>
      }
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('rentPaid')">
        <label class="field-label" for="rentPaid">
          Section 80GG — Rent Paid (no HRA)
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="rentPaid" type="number"
            [(ngModel)]="rentPaid"
            [min]="C.rentPaid.min" [max]="C.rentPaid.max" [step]="C.rentPaid.step"
            (ngModelChange)="onFieldChange('rentPaid')"
            (blur)="onFieldBlur('rentPaid')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Only applies if you're not claiming HRA exemption above. Capped at {{ fmt.currency(60000) }}/yr and 25% of income.</div>
        @if (hasError('rentPaid')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('rentPaid') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('ltaExemption')">
        <label class="field-label" for="ltaExemption">
          LTA Exemption
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="ltaExemption" type="number"
            [(ngModel)]="ltaExemption"
            [min]="C.ltaExemption.min" [max]="C.ltaExemption.max" [step]="C.ltaExemption.step"
            (ngModelChange)="onFieldChange('ltaExemption')"
            (blur)="onFieldBlur('ltaExemption')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Exempt portion of Leave Travel Allowance (Sec 10(5)).</div>
        @if (hasError('ltaExemption')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('ltaExemption') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('section80EEA')">
        <label class="field-label" for="section80EEA">
          Section 80EEA — Affordable Housing Interest
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="section80EEA" type="number"
            [(ngModel)]="section80EEA"
            [min]="C.section80EEA.min" [max]="C.section80EEA.max" [step]="C.section80EEA.step"
            (ngModelChange)="onFieldChange('section80EEA')"
            (blur)="onFieldBlur('section80EEA')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Additional home loan interest, over and above Sec 24(b). Capped at {{ fmt.currency(C.section80EEA.max) }}.</div>
        @if (hasError('section80EEA')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('section80EEA') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('section80EEB')">
        <label class="field-label" for="section80EEB">
          Section 80EEB — EV Loan Interest
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="section80EEB" type="number"
            [(ngModel)]="section80EEB"
            [min]="C.section80EEB.min" [max]="C.section80EEB.max" [step]="C.section80EEB.step"
            (ngModelChange)="onFieldChange('section80EEB')"
            (blur)="onFieldBlur('section80EEB')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Interest on an electric vehicle loan. Capped at {{ fmt.currency(C.section80EEB.max) }}.</div>
        @if (hasError('section80EEB')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('section80EEB') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('section80DDB')">
        <label class="field-label" for="section80DDB">
          Section 80DDB — Medical Treatment
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="section80DDB" type="number"
            [(ngModel)]="section80DDB"
            [min]="C.section80DDB.min" [max]="C.section80DDB.max" [step]="C.section80DDB.step"
            (ngModelChange)="onFieldChange('section80DDB')"
            (blur)="onFieldBlur('section80DDB')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Treatment for specified diseases. Capped at {{ fmt.currency(ageCategory === 'below60' ? 40000 : 100000) }} for your age category.</div>
        @if (hasError('section80DDB')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('section80DDB') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('professionalTax')">
        <label class="field-label" for="professionalTax">
          Professional Tax
          <span class="optional-tag">optional</span>
        </label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="professionalTax" type="number"
            [(ngModel)]="professionalTax"
            [min]="C.professionalTax.min" [max]="C.professionalTax.max" [step]="C.professionalTax.step"
            (ngModelChange)="onFieldChange('professionalTax')"
            (blur)="onFieldBlur('professionalTax')"
            placeholder="e.g. 2,500"
            autocomplete="off"/>
        </div>
        <div class="field-hint">State professional tax deducted from salary (Sec 16(iii)). Capped at {{ fmt.currency(C.professionalTax.max) }}.</div>
        @if (hasError('professionalTax')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('professionalTax') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group">
        <!-- Group heading for the disability-dependent toggle buttons below, wired via aria-labelledby -->
        <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
        <label class="field-label" id="disability-dependent-label">
          Section 80DD — Disability of Dependent
          <span class="optional-tag">optional</span>
        </label>
        <div class="mode-toggle" role="group" aria-labelledby="disability-dependent-label">
          <button type="button" class="mode-btn" [class.active]="section80DD==='none'" (click)="setDisabilityDependent('none')">
            None
          </button>
          <button type="button" class="mode-btn" [class.active]="section80DD==='normal'" (click)="setDisabilityDependent('normal')">
            40–79%
          </button>
          <button type="button" class="mode-btn" [class.active]="section80DD==='severe'" (click)="setDisabilityDependent('severe')">
            80%+ (Severe)
          </button>
        </div>
        <div class="field-hint">Fixed deduction: ₹75,000 (40–79%) or ₹1,25,000 (80%+ severe disability).</div>
      </div>

      <div class="field-group">
        <!-- Group heading for the self-disability toggle buttons below, wired via aria-labelledby -->
        <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
        <label class="field-label" id="disability-self-label">
          Section 80U — Self Disability
          <span class="optional-tag">optional</span>
        </label>
        <div class="mode-toggle" role="group" aria-labelledby="disability-self-label">
          <button type="button" class="mode-btn" [class.active]="section80U==='none'" (click)="setDisabilitySelf('none')">
            None
          </button>
          <button type="button" class="mode-btn" [class.active]="section80U==='normal'" (click)="setDisabilitySelf('normal')">
            40–79%
          </button>
          <button type="button" class="mode-btn" [class.active]="section80U==='severe'" (click)="setDisabilitySelf('severe')">
            80%+ (Severe)
          </button>
        </div>
        <div class="field-hint">Fixed deduction: ₹75,000 (40–79%) or ₹1,25,000 (80%+ severe disability).</div>
      </div>
    </div>

    <!-- ── DEDUCTIONS (BOTH REGIMES) ──────────────────────────────── -->
    <div class="subsection-title">Employer NPS Contribution <span class="optional-tag">both regimes</span></div>

    <div class="field-group" [class.has-error]="hasError('section80CCD2')">
      <label class="field-label" for="section80CCD2">
        Section 80CCD(2)
        <span class="optional-tag">optional</span>
      </label>
      <div class="input-shell prefix">
        <span class="affix">₹</span>
        <input id="section80CCD2" type="number"
          [(ngModel)]="section80CCD2"
          [min]="C.section80CCD2.min" [max]="C.section80CCD2.max" [step]="C.section80CCD2.step"
          (ngModelChange)="onFieldChange('section80CCD2')"
          (blur)="onFieldBlur('section80CCD2')"
          placeholder="e.g. 0"
          autocomplete="off"/>
      </div>
      <div class="field-hint">Employer's NPS contribution from Form 16. Applies in both regimes — capped at 10% of salary (old regime) or 14% (new regime).</div>
      @if (hasError('section80CCD2')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('section80CCD2') }}
        </div>
      }
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Better Regime</div>
        <div class="sc-value">{{ result.betterRegime === 'new' ? 'New Regime' : 'Old Regime' }}</div>
        <div class="sc-sub">saves {{ fmt.currency(result.savings) }}</div>
      </div>
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">New Regime Tax</div>
        <div class="sc-value">{{ fmt.currency(result.newRegime.totalTax) }}</div>
        <div class="sc-sub">take-home {{ fmt.currency(result.newRegime.takeHome) }}</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Old Regime Tax</div>
        <div class="sc-value">{{ fmt.currency(result.oldRegime.totalTax) }}</div>
        <div class="sc-sub">take-home {{ fmt.currency(result.oldRegime.takeHome) }}</div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Regime Comparison</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Component</th>
              <th class="t-balance">New Regime</th>
              <th class="t-balance">Old Regime</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="t-left">Gross Total Income</td>
              <td class="t-balance">{{ fmt.currency(result.newRegime.grossTotalIncome) }}</td>
              <td class="t-balance">{{ fmt.currency(result.oldRegime.grossTotalIncome) }}</td>
            </tr>
            <tr>
              <td class="t-left">Standard Deduction</td>
              <td class="t-balance">{{ fmt.currency(result.newRegime.standardDeduction) }}</td>
              <td class="t-balance">{{ fmt.currency(result.oldRegime.standardDeduction) }}</td>
            </tr>
            <tr>
              <td class="t-left">Other Deductions</td>
              <td class="t-balance">{{ fmt.currency(result.newRegime.totalDeductions) }}</td>
              <td class="t-balance">{{ fmt.currency(result.oldRegime.totalDeductions) }}</td>
            </tr>
            <tr>
              <td class="t-left"><strong>Taxable Income</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.newRegime.taxableIncome) }}</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.oldRegime.taxableIncome) }}</strong></td>
            </tr>
            <tr>
              <td class="t-left">Tax Before Rebate</td>
              <td class="t-balance">{{ fmt.currency(result.newRegime.taxBeforeRebate) }}</td>
              <td class="t-balance">{{ fmt.currency(result.oldRegime.taxBeforeRebate) }}</td>
            </tr>
            <tr>
              <td class="t-left">Section 87A Rebate</td>
              <td class="t-balance">{{ fmt.currency(result.newRegime.rebate) }}</td>
              <td class="t-balance">{{ fmt.currency(result.oldRegime.rebate) }}</td>
            </tr>
            <tr>
              <td class="t-left">Health & Education Cess (4%)</td>
              <td class="t-balance">{{ fmt.currency(result.newRegime.cess) }}</td>
              <td class="t-balance">{{ fmt.currency(result.oldRegime.cess) }}</td>
            </tr>
            <tr>
              <td class="t-left"><strong>Total Tax Payable</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.newRegime.totalTax) }}</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.oldRegime.totalTax) }}</strong></td>
            </tr>
            <tr>
              <td class="t-left"><strong>Take-home Income</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.newRegime.takeHome) }}</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.oldRegime.takeHome) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="action-row mt-sm">
        <button type="button" class="btn btn-secondary" (click)="toggleBreakup()">
          {{ showBreakup ? 'Hide' : 'Show' }} slab-wise tax breakup
        </button>
      </div>

      @if (showBreakup) {
        <div class="field-row-2 mt-sm">
          <div>
            <div class="subsection-title">New Regime Slabs</div>
            <div class="tbl-wrap">
              <table>
                <thead>
                  <tr><th class="t-left">Slab</th><th>Rate</th><th class="t-balance">Tax</th></tr>
                </thead>
                <tbody>
                  @for (s of result.newRegime.slabBreakup; track s.rangeLabel) {
                    <tr>
                      <td class="t-left">{{ s.rangeLabel }}</td>
                      <td>{{ s.rate * 100 }}%</td>
                      <td class="t-balance">{{ fmt.currency(s.taxInSlab) }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <div class="subsection-title">Old Regime Slabs</div>
            <div class="tbl-wrap">
              <table>
                <thead>
                  <tr><th class="t-left">Slab</th><th>Rate</th><th class="t-balance">Tax</th></tr>
                </thead>
                <tbody>
                  @for (s of result.oldRegime.slabBreakup; track s.rangeLabel) {
                    <tr>
                      <td class="t-left">{{ s.rangeLabel }}</td>
                      <td>{{ s.rate * 100 }}%</td>
                      <td class="t-balance">{{ fmt.currency(s.taxInSlab) }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">New Tax Regime Slabs (FY 2025-26)</h2>
    <div class="tbl-wrap">
      <table>
        <thead>
          <tr><th class="t-left">Income Slab</th><th>Tax Rate</th></tr>
        </thead>
        <tbody>
          <tr><td class="t-left">Up to ₹4,00,000</td><td>Nil</td></tr>
          <tr><td class="t-left">₹4,00,000 – ₹8,00,000</td><td>5%</td></tr>
          <tr><td class="t-left">₹8,00,000 – ₹12,00,000</td><td>10%</td></tr>
          <tr><td class="t-left">₹12,00,000 – ₹16,00,000</td><td>15%</td></tr>
          <tr><td class="t-left">₹16,00,000 – ₹20,00,000</td><td>20%</td></tr>
          <tr><td class="t-left">₹20,00,000 – ₹24,00,000</td><td>25%</td></tr>
          <tr><td class="t-left">Above ₹24,00,000</td><td>30%</td></tr>
        </tbody>
      </table>
    </div>
    <p>
      Standard deduction of &#8377;75,000 applies under the new regime, and Section 87A rebate
      brings tax to zero for taxable income up to &#8377;12,00,000.
    </p>

    <h2 class="section-title mt-sm">Old Tax Regime Slabs</h2>
    <div class="tbl-wrap">
      <table>
        <thead>
          <tr><th class="t-left">Income Slab</th><th>Tax Rate</th></tr>
        </thead>
        <tbody>
          <tr><td class="t-left">Up to ₹2,50,000</td><td>Nil</td></tr>
          <tr><td class="t-left">₹2,50,000 – ₹5,00,000</td><td>5%</td></tr>
          <tr><td class="t-left">₹5,00,000 – ₹10,00,000</td><td>20%</td></tr>
          <tr><td class="t-left">Above ₹10,00,000</td><td>30%</td></tr>
        </tbody>
      </table>
    </div>
    <p>
      Standard deduction of &#8377;50,000 applies under the old regime, along with any Section 80C,
      80D and other deductions you claim. Section 87A rebate brings tax to zero for taxable income
      up to &#8377;5,00,000. Slabs shown are for individuals below 60; senior and super senior
      citizens get a higher basic exemption under the old regime.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Which regime should I choose?</h3>
      <p>
        If your eligible deductions (80C, 80D, HRA, home loan interest, etc.) are large relative to
        your income, the old regime often works out cheaper. If you claim few deductions, the new
        regime's lower slab rates usually win. This calculator compares both for your numbers.
      </p>
    </div>
    <div class="faq-item">
      <h3>Can I switch between regimes every year?</h3>
      <p>
        Salaried individuals can choose either regime each financial year. Those with business
        income face restrictions on switching back to the old regime after opting out.
      </p>
    </div>
    <div class="faq-item">
      <h3>Does my age category affect the tax calculation?</h3>
      <p>
        Age only changes the old regime's basic exemption limit and the Section 80D health insurance
        cap — the new regime slabs are the same for everyone. Senior citizens (60–80) get a higher
        exemption limit and 80D cap, and super senior citizens (80+) get an even higher exemption
        limit.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/old-vs-new-tax-regime">Old vs new tax regime: which saves more in FY 2026-27?</a>
    </p>
  </div>
</section>
